import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  Grid,
  Card,
  Backdrop,
  CircularProgress,
  Box,
  Button,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import MDDatePicker from 'components/MDDatePicker';

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

const RELIGION_OPTIONS = [
  'HINDUISM',
  'ISLAM',
  'CHRISTIANITY',
  'SIKHISM',
  'BUDDHISM',
  'JAINISM',
  'OTHERS',
];

const INDIA_COUNTRY_ID = 100;

const calcAge = (dobStr) => {
  if (!dobStr) return '';
  const today = dayjs();
  const dob = dayjs(dobStr);
  if (!dob.isValid()) return '';
  return today.diff(dob, 'year');
};

function DonorEdit() {
  const { external_code } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileUploaded, setProfileUploaded] = useState(false);
  const [countries, setCountries] = useState([]);
  const [donor, setDonor] = useState(null);

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    marital_status: '',
    date_of_birth: '',
    age: '',
    email_address: '',
    religion: '',
    nationality: '',
    occupation: '',
    identification_mark: '',
    country_id: INDIA_COUNTRY_ID,
    remarks: '',
  });

  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  useEffect(() => {
    api.master.getActiveCountries().then((res) => {
      if (res?.data?.countries?.length) setCountries(res.data.countries);
    });
  }, []);

  useEffect(() => {
    if (!external_code) return;
    setLoading(true);
    api.contacts
      .getDonorDetails(external_code)
      .then((res) => {
        const d = res?.data?.donor || res?.data;
        if (d) {
          setDonor(d);
          const dobStr = d.date_of_birth ? d.date_of_birth.substring(0, 10) : '';
          setForm({
            first_name: d.first_name || '',
            middle_name: d.middle_name || '',
            last_name: d.last_name || '',
            gender: d.gender || '',
            marital_status: d.marital_status || '',
            date_of_birth: dobStr,
            age: dobStr ? String(calcAge(dobStr)) : (d.age ? String(d.age) : ''),
            email_address: d.email_address || '',
            religion: d.religion || '',
            nationality: d.nationality || '',
            occupation: d.occupation || '',
            identification_mark: d.identification_mark || '',
            country_id: d.country_id || INDIA_COUNTRY_ID,
            remarks: d.remarks || '',
          });
          if (d.profile_picture_base_url && d.profile_picture_path) {
            setProfilePreview(d.profile_picture_base_url + d.profile_picture_path);
          }
        }
      })
      .catch(() => {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load donor data' });
        navigate('/donor-list');
      })
      .finally(() => setLoading(false));
  }, [external_code]);

  const handleFormChange = (field, value) => {
    const upper = typeof value === 'string' && field !== 'email_address' ? value.toUpperCase() : value;
    setForm((p) => ({ ...p, [field]: upper }));
  };

  const handleCountryChange = (countryId) => {
    const selected = countries.find((c) => c.id === countryId);
    setForm((p) => ({
      ...p,
      country_id: countryId,
      nationality: selected ? (selected.nationality || selected.country_name) : p.nationality,
    }));
  };

  const isAgeValid = () => {
    if (!form.date_of_birth || !form.gender) return true;
    const age = Number(calcAge(form.date_of_birth));
    if (form.gender === 'F' && age < 18) return false;
    if (form.gender === 'M' && age < 21) return false;
    return true;
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    setProfilePreview(URL.createObjectURL(file));
    setProfileUploaded(false);
  };

  const handleUploadPhoto = async () => {
    if (!profileImage) return;
    if (!donor?.id) {
      Swal.fire({ icon: 'warning', title: 'Error', text: 'Donor ID not found. Please reload the page.' });
      return;
    }
    setProfileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', profileImage);
      fd.append('patient_id', String(donor.id));
      await api.contacts.uploadDonorProfileImage(fd);
      setProfileUploaded(true);
      Swal.fire({ icon: 'success', title: 'Photo uploaded!', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Upload failed',
        text: err?.response?.data?.message || err?.message || 'Failed to upload profile photo',
      });
    } finally {
      setProfileUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.first_name) {
      Swal.fire({ icon: 'warning', title: 'Validation', text: 'First name is required' });
      return;
    }
    if (!form.gender) {
      Swal.fire({ icon: 'warning', title: 'Validation', text: 'Gender is required' });
      return;
    }
    if (form.date_of_birth && !isAgeValid()) {
      Swal.fire({
        icon: 'warning',
        title: 'Age Validation',
        text: form.gender === 'F' ? 'Female donors must be at least 18 years old' : 'Male donors must be at least 21 years old',
      });
      return;
    }

    const payload = {
      first_name: form.first_name,
      middle_name: form.middle_name || undefined,
      last_name: form.last_name || undefined,
      gender: form.gender,
      marital_status: form.marital_status || undefined,
      date_of_birth: form.date_of_birth || undefined,
      age: form.age ? Number(form.age) : undefined,
      email_address: form.email_address || undefined,
      religion: form.religion || undefined,
      nationality: form.nationality || undefined,
      occupation: form.occupation || undefined,
      identification_mark: form.identification_mark || undefined,
      country_id: form.country_id,
      remarks: form.remarks || undefined,
    };

    setSaving(true);
    try {
      await api.contacts.editDonor(donor.id, payload);

      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: 'Donor updated successfully.',
        timer: 1500,
        showConfirmButton: false,
      }).then(() => navigate(`/donor-details/${external_code}`));
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.message || 'Failed to update donor',
      });
    } finally {
      setSaving(false);
    }
  };

  const quillModules = {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop open={loading || saving || profileUploading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <MDBox pt={4} pb={4} px={2}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate(`/donor-details/${external_code}`)}>
            <ArrowBackIcon />
          </IconButton>
          <MDTypography variant="h4" fontWeight="bold">Edit Donor</MDTypography>
          {donor && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {donor.external_code}
            </Typography>
          )}
        </Box>

        {/* Profile Picture */}
        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>Profile Picture</MDTypography>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" alignItems="center" gap={3}>
            <Box
              sx={{
                width: 120, height: 120, border: '2px dashed #00798E', borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', background: '#f7f9fa', overflow: 'hidden',
              }}
              onClick={() => document.getElementById('donor-edit-profile-upload').click()}
            >
              {profilePreview ? (
                <img src={profilePreview} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Box textAlign="center">
                  <PersonIcon sx={{ fontSize: 40, color: '#aaa' }} />
                  <Typography variant="caption" display="block" color="text.secondary">Click to change</Typography>
                </Box>
              )}
              <input
                id="donor-edit-profile-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleProfileImageChange}
              />
            </Box>
            <Box>
              {profileImage && (
                <Typography variant="body2" mb={1}>{profileImage.name}</Typography>
              )}
              {profileUploaded ? (
                <Typography variant="body2" color="success.main" display="flex" alignItems="center" gap={0.5}>
                  <CloudDoneIcon fontSize="small" /> Photo uploaded successfully
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Click the photo to select a new image, then click Upload.
                  </Typography>
                  {profileImage && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={profileUploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
                      onClick={handleUploadPhoto}
                      disabled={profileUploading}
                      sx={{ color: '#fff', bgcolor: '#00798E', '&:hover': { bgcolor: '#005f72' } }}
                    >
                      {profileUploading ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Card>

        {/* Personal Info */}
        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>Personal Information</MDTypography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="First Name *" value={form.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Middle Name" value={form.middle_name}
                onChange={(e) => handleFormChange('middle_name', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Last Name" value={form.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Gender *</InputLabel>
                <Select
                  value={form.gender} label="Gender *"
                  onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                  sx={{ height: 45 }}
                >
                  {GENDER_OPTIONS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  value={form.marital_status} label="Marital Status"
                  onChange={(e) => setForm((p) => ({ ...p, marital_status: e.target.value }))}
                  sx={{ height: 45 }}
                >
                  {MARITAL_STATUS_OPTIONS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* DOB */}
            <Grid item xs={12} sm={4}>
              <MDDatePicker
                label="Date of Birth *"
                value={form.date_of_birth}
                onChange={(date) =>
                  setForm((p) => ({ ...p, date_of_birth: date || '', age: date ? String(calcAge(date)) : '' }))
                }
                disableFuture
                textFieldProps={{
                  fullWidth: true,
                  error: !!form.date_of_birth && !isAgeValid(),
                  helperText:
                    form.date_of_birth && !isAgeValid()
                      ? form.gender === 'F' ? 'Female donors must be 18+' : 'Male donors must be 21+'
                      : '',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Age (Auto-calculated)" value={form.age}
                InputProps={{ readOnly: true }} sx={{ background: '#f5f5f5' }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                value="India"
                InputProps={{ readOnly: true }}
                sx={{ background: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Nationality" value={form.nationality}
                onChange={(e) => handleFormChange('nationality', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Email Address" value={form.email_address}
                onChange={(e) => setForm((p) => ({ ...p, email_address: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Religion</InputLabel>
                <Select
                  value={form.religion} label="Religion"
                  onChange={(e) => setForm((p) => ({ ...p, religion: e.target.value }))}
                  sx={{ height: 45 }}
                >
                  {RELIGION_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Occupation" value={form.occupation}
                onChange={(e) => handleFormChange('occupation', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Identification Mark" value={form.identification_mark}
                onChange={(e) => handleFormChange('identification_mark', e.target.value)}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>Remarks</Typography>
              <Box
                sx={{
                  '.ql-container': { minHeight: 80, borderRadius: '0 0 4px 4px' },
                  '.ql-toolbar': { borderRadius: '4px 4px 0 0' },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={form.remarks}
                  onChange={(val) => setForm((p) => ({ ...p, remarks: val }))}
                  modules={quillModules}
                />
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Save */}
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <button
            type="button"
            onClick={() => navigate(`/donor-details/${external_code}`)}
            style={{
              padding: '10px 24px',
              background: '#e0e0e0',
              color: '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.3s ease',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#bdbdbd')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#e0e0e0')}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{
              padding: '10px 24px',
              background: saving ? '#cccccc' : '#00798E',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'background 0.3s ease',
            }}
            onMouseOver={(e) => { if (!saving) e.currentTarget.style.background = '#006379'; }}
            onMouseOut={(e) => { if (!saving) e.currentTarget.style.background = '#00798E'; }}
          >
            <SaveIcon fontSize="small" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </Box>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default DonorEdit;
